<script lang="ts">
  import {createEventDispatcher, getContext, onDestroy, onMount} from 'svelte';
  import Swal from 'sweetalert2';

  import {appEvents} from '@/services';
  import {formats, project, user, videos} from '@/store';

  import {tooltip} from '@/actions/tippy-action';
  import {FileReencodeToggle} from '@/components';
  import {UploadSource} from '@/types/common';
  import {AvailableFormats} from '@/types/upload';
  import {validateUrl} from '@/utils/common';
  import Dialog from '../../../components/dialog/Dialog.svelte';
  import LanguagePicker from '../LanguagePicker.svelte';
  import {TrimVideoDialog, TrimVideoEvent} from './TrimVideoDialog';
  const dispatch = createEventDispatcher();

  export let isUploadYoutubeDialogOpen = false;
  export let videoFilesLength = 0;
  export let isUploading: boolean;
  export let source: UploadSource;

  const simpleModal: {open: Function; close: Function} = getContext('simple-modal');

  let isFetchingFormats = false;
  let retryCount = 0;
  let maxReties = 1;
  let reencode: boolean | undefined = false;
  let isTrimVideoDialogOpen = false;
  let trimVideoOptions: TrimVideoEvent | null = null;
  let isGeneratingTimeline = false;
  let youtubeUrl: string = '';
  let selectedLanguage: string = 'English';
  let selectedQuality: number | null;
  let selectedFormat: any;
  let highestAvailable: AvailableFormats;
  let errorMessage: string = '';
  let warningMessage: string = '';
  let toggleDropdown: boolean = false;
  let sourceLabel: string = 'YouTube';

  // Temporary variable to store trimDisabled - this depends on the input source
  let trimDisabled: boolean = false;

  const reset = () => {
    isGeneratingTimeline = false;
    isTrimVideoDialogOpen = false;
    isUploadYoutubeDialogOpen = false;
    selectedQuality = null;
    trimVideoOptions = null;
    youtubeUrl = '';
    formats.reset();
  };

  const onYoutubeSuccess = async (data: any) => {
    console.log('[yt-success-data]', data);
    isUploading = false;
    youtubeUrl = '';
  };

  const onYoutubeFailure = (data: any) => {
    console.error('[yt-error]', data);
    if (retryCount == maxReties) {
      Swal.fire({
        titleText: 'Problem with upload',
        html:
          '<p>It seems YouTube has not yet made the compatible video format available for download. Please try again later.</p><br>' +
          '<p>Alternatively, you can upload from your computer if you have a local copy so you can use the video now.</p>',
        footer: '<a href="http://sermonshots.com/contact">Contact us if you have any questions.</a>',
        icon: 'error'
      });
    }
    youtubeUrl = '';
    isUploading = false;
  };

  async function submitYoutubeVideo() {
    const language = selectedLanguage;
    isUploading = true;

    try {
      appEvents.off('youtube-download-success', onYoutubeSuccess);
      appEvents.off('youtube-download-failed', onYoutubeFailure);

      await project
        .uploadYoutubeVideo({
          url: youtubeUrl,
          language,
          format: selectedFormat,
          source,
          videoId: undefined,
          options: {
            reencode: Boolean(trimVideoOptions) || reencode,
            ...(trimVideoOptions && {
              trim: {
                start: trimVideoOptions.start,
                end: trimVideoOptions.end
              }
            })
          }
        })
        .then(async () => {
          $formats.items = [];
          await videos.getVideoList(1, videoFilesLength);
        });

      reset();
    } catch (error) {
      console.error('youtube video upload error', error);

      if (retryCount > maxReties) {
        isUploading = isUploadYoutubeDialogOpen = false;
        youtubeUrl = '';
        console.error('reached maximum retries for uploading youtube video', error);
        return;
      }

      retryCount++;
      submitYoutubeVideo();
    }
  }

  async function fetchAvailableFormats(url: string) {
    errorMessage = '';
    isFetchingFormats = true;

    formats
      .fetchAvailableFormats(url, source)
      .then(() => {
        if ($formats.items.length == 0) {
          errorMessage = 'Video Quality not available this time. Try again later.';
        } else {
          highestAvailable = $formats.items[0];
          selectQuality(highestAvailable, false);
        }
      })
      .catch(e => {
        console.error('error fetching available formats', e);
        errorMessage = 'Unable to Fetch Video Information';
      })
      .finally(() => {
        isFetchingFormats = false;
      });
  }

  async function fetchVideoPreview() {
    isGeneratingTimeline = true;

    formats
      .fetchVideoPreview(youtubeUrl, source)
      .then(() => {
        isUploadYoutubeDialogOpen = false;
        isTrimVideoDialogOpen = true;
      })
      .finally(() => {
        isGeneratingTimeline = false;
      });
  }

  function selectQuality(item: any, toggle = true) {
    selectedQuality = item.label;
    selectedFormat = item.value;
    warningMessage = '';
    if (toggle) {
      toggleDropdown = !toggleDropdown;
    }
    return null;
  }

  $: if (youtubeUrl) {
    setTimeout(() => {
      if (source === UploadSource.DROPBOX || source === UploadSource.GOOGLEDRIVE) {
        fetchAvailableFormats(youtubeUrl);
      } else {
        const validUrl = validateUrl(youtubeUrl);
        if (!validUrl) {
          errorMessage = 'Invalid URL';
          return;
        }
        youtubeUrl = validUrl;
        fetchAvailableFormats(youtubeUrl);
      }
    }, 1000);
  }

  $: displayClass =
    toggleDropdown && $formats.hasData
      ? 'w-full flex flex-col absolute top-0 left-0 rounded-lg border-y border bg-white z-10'
      : 'hidden';

  $: {
    trimDisabled = false;

    switch (source) {
      case UploadSource.YOUTUBE:
        sourceLabel = 'YouTube';
        break;
      case UploadSource.VIMEO:
        sourceLabel = 'Vimeo';
        trimDisabled = true;
        break;
      case UploadSource.FACEBOOK:
        sourceLabel = 'Facebook';
        break;
      case UploadSource.DROPBOX:
        sourceLabel = 'Dropbox';
        break;
      case UploadSource.GOOGLEDRIVE:
        sourceLabel = 'Google Drive';
        trimDisabled = true;
        break;
    }
  }

  const preferencesSubscription = user.subscribe(user => {
    const resync = user?.preferences?.resync;
    const language = user?.preferences?.language;
    reencode = resync;
    selectedLanguage = language || 'English';
  });

  onMount(() => {
    // Reencode Controls
    if (source === UploadSource.DROPBOX || source === UploadSource.GOOGLEDRIVE) {
      reencode = $user?.preferences?.resync;
    } else {
      reencode = false;
    }

    selectedLanguage = $user?.preferences?.language || 'English';
  });

  onDestroy(() => preferencesSubscription());

  const handleFormSubmit = (trim: boolean = false) => {
    if (!simpleModal || typeof simpleModal.open !== 'function' || typeof simpleModal.close !== 'function')
      return console.log('simple-modal not found.', {simpleModal});
    if (youtubeUrl === '') return console.log('youtube url is invalid');

    if (!selectedFormat) {
      warningMessage = 'Please select a video quality before uploading video.';
      return;
    }

    trim ? fetchVideoPreview() : submitYoutubeVideo();
  };

  const handleTrimAndUpload = (e: CustomEvent<TrimVideoEvent>) => {
    trimVideoOptions = e.detail;
    isTrimVideoDialogOpen = false;
    submitYoutubeVideo();
  };

  const handleCloseDialog = () => {
    reset();
    isUploadYoutubeDialogOpen = false;
    isUploading = false;
  };

  $: isDisabled = isUploading || isGeneratingTimeline || isFetchingFormats || !youtubeUrl;
</script>

<Dialog bind:isDialogOpen={isUploadYoutubeDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-end justify-center lg:items-center">
    <form
      class="pointer-events-auto h-auto w-full min-w-[280px] rounded-t-2xl border border-gray-300 bg-white p-12 shadow-lg dark:border-zinc-600 dark:bg-zinc-700 lg:max-w-[680px] lg:rounded-3xl"
    >
      <div class="flex pb-2 text-black dark:text-zinc-200">
        <div class="flex items-center gap-2 py-1.5">
          <h1 class="text-lg font-bold">Upload {sourceLabel} Video</h1>
        </div>
        <button on:click|preventDefault={handleCloseDialog} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
          <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
        </button>
      </div>
      <!-- <hr class="border-t border-t-gray-300 dark:border-zinc-600"> -->
      <div class="grid pt-6">
        <div class="py-2">
          <label for="youtube-url" class="block text-sm font-medium text-gray-800 dark:text-zinc-200">
            {sourceLabel} Link
            <i
              use:tooltip={'Please make sure your video is shared publicly.'}
              class="fa-regular fa-circle-question text-xs text-black"
            ></i>
          </label>
          <div class="flex pt-2">
            <input
              bind:value={youtubeUrl}
              name="youtube-url"
              type="url"
              placeholder="Enter URL here..."
              class="block w-full rounded-lg border border-y border-gray-300 bg-white px-4 py-2.5 text-gray-800 placeholder-gray-300 focus:border-primary focus:outline-none focus:ring focus:ring-primary focus:ring-opacity-40 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:placeholder-zinc-500 dark:focus:border-blue-300"
            />
          </div>
        </div>
        <div class="py-2">
          <label for="pick-quality" class="block text-sm font-medium text-gray-800 dark:text-zinc-200"
            >Video Quality</label
          >
          <div class="{$formats.hasData ? '' : 'disabled'} relative flex flex-col gap-1 pt-2">
            <div
              class="{$formats.hasData
                ? 'text-gray-800'
                : 'pointer-events-none text-gray-300'} relative flex w-full pt-2"
            >
              <span
                on:click={() => (toggleDropdown = !toggleDropdown)}
                on:keydown={() => (toggleDropdown = !toggleDropdown)}
                class="block w-full rounded-lg border border-y bg-white px-4 py-2.5 dark:bg-zinc-800 {warningMessage !=
                ''
                  ? 'border-primary'
                  : 'border-gray-300'} {!selectQuality ? 'dark:text-zinc-200' : 'dark:text-zinc-500'}"
              >
                {!selectedQuality ? 'Choose video quality' : selectedQuality}
                <i class="fa-solid fa-angle-down float-right py-1"> </i>
              </span>
              <div class={displayClass}>
                {#if !$formats.has1080}
                  <div
                    class="disabled block cursor-pointer border-y px-4 py-3 text-gray-300 hover:bg-gray-100"
                    on:click={() => dispatch('toggle-1080-dialog')}
                    on:keydown={() => dispatch('toggle-1080-dialog')}
                  >
                    <span>1080p <i class="fa-regular fa-circle-question"></i></span>
                  </div>
                {/if}
                {#each $formats.items as item}
                  <div
                    class="block cursor-pointer border-y px-4 py-3 hover:bg-gray-100"
                    on:click={selectQuality(item)}
                    on:keydown={selectQuality(item)}
                  >
                    <span>
                      {item.label}
                      {#if highestAvailable == item}
                        <span class="ml-1 rounded-xl bg-primary px-2 py-1 text-xs uppercase text-white"
                          >Recommended</span
                        >
                      {/if}
                    </span>
                  </div>
                {/each}
              </div>
            </div>
            {#if warningMessage != ''}
              <span class="text-sm text-red-500">{warningMessage}</span>
            {/if}
          </div>
          <div class="py-2">
            <LanguagePicker />
          </div>
          {#if source === UploadSource.DROPBOX || source === UploadSource.GOOGLEDRIVE}
            <div class="py-2">
              <label for="username" class="block text-sm font-medium text-gray-800 dark:text-zinc-200"
                >Other Options</label
              >
              <FileReencodeToggle bind:reencode />
            </div>
          {/if}
          <!-- {#if source === UploadSource.YOUTUBE}
            <div class="mt-2 flex flex-col space-y-2 rounded-lg border border-amber-700 bg-amber-200 px-4 py-2">
              <div class="py-2">
                <p class="text-justify text-sm text-amber-700">
                  <span class="font-bold"><i class="fa-regular fa-triangle-exclamation"></i></span> Some YouTube video resolutions
                  are unavailable at the moment due to updates on YouTube's end. We are still working on a fix for this.
                  Please consider uploading from a different source in the meantime if you want higher resolution videos
                  for your clips.
                </p>
              </div>
            </div>
          {/if} -->
        </div>
        <!-- <hr class="border-t border-t-gray-300 dark:border-zinc-600"> -->
        <div class="flex w-full items-center justify-between pt-6">
          <div class="flex justify-start gap-2">
            {#if isFetchingFormats}
              <div
                class="spinner h-4 w-4 !border-zinc-100 !border-t-primary dark:!border-zinc-700 dark:!border-t-primary"
              ></div>
              <p class="text-sm text-gray-800">Fetching Video Information</p>
            {/if}
            {#if isGeneratingTimeline}
              <div
                class="spinner h-4 w-4 !border-zinc-100 !border-t-primary dark:!border-zinc-700 dark:!border-t-primary"
              ></div>
              <p class="text-sm text-gray-800">Generating Preview</p>
            {/if}
            {#if isUploading}
              <div
                class="spinner h-4 w-4 !border-zinc-100 !border-t-primary dark:!border-zinc-700 dark:!border-t-primary"
              ></div>
              <p class="text-sm text-gray-800">Preparing Video For Upload</p>
            {/if}
            {#if errorMessage != ''}
              <span class="text-sm text-red-500">{errorMessage}</span>
            {/if}
          </div>
          <div class="flex justify-end gap-2 pt-6">
            <button
              on:click|preventDefault={handleCloseDialog}
              type="button"
              class="btn btn-tertiary !text-sermon-base-300"
              disabled={isUploading}
            >
              <span>Cancel</span>
            </button>
            {#if youtubeUrl}
              <button
                type="button"
                on:click|preventDefault={() => handleFormSubmit(true)}
                class="btn btn-secondary"
                disabled={isDisabled || trimDisabled}
              >
                <span class="font-medium">Trim Video</span>
                {#if trimDisabled}
                  <i use:tooltip={'Coming soon for this format'} class="fa fa-regular fa-circle-question text-sm"></i>
                {/if}
              </button>
            {/if}
            <button
              type="button"
              on:click|preventDefault={() => handleFormSubmit(false)}
              class="btn btn-primary"
              disabled={isDisabled}
            >
              <span class="font-medium">Upload</span>
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</Dialog>

{#if $formats.preview && isTrimVideoDialogOpen}
  <TrimVideoDialog
    video={$formats.preview.src}
    timeline={$formats.preview.timeline}
    on:submit={handleTrimAndUpload}
    on:close={handleCloseDialog}
  />
{/if}
